/**
 * N8nChat — React wrapper around n8n's `@n8n/chat` floating widget.
 *
 * Why this exists:
 * - The previous `loadN8nChat()` method on SystemProgramming did raw
 *   `document.createElement` + `appendChild` to bootstrap the widget,
 *   plus inline `<style>` injection. PR feedback (Jorge) flagged that
 *   as anti-React. This wraps the same widget with proper lifecycle:
 *   ref-bound host node, mount on `componentDidMount`, destroy on
 *   `componentWillUnmount`, reactive metadata updates on
 *   `componentDidUpdate`.
 *
 * Why we still inject a CDN script:
 * - `@n8n/chat` ships as an ESM CDN bundle. Webpack/CRA in this app
 *   doesn't handle dynamic imports of remote URLs cleanly (no
 *   `webpackIgnore` support in the installed CRA toolchain). The
 *   pragmatic choice is to inject one `<script type=module>` per
 *   page lifecycle and bridge to React via `window.n8nChatReady`.
 *
 * Metadata:
 * - The `metadata` prop is a JSON-serializable object that gets
 *   shipped with every webhook call to n8n. Use it to give the
 *   chatbot context about what the user is currently looking at
 *   (active design, system, account, step). The n8n workflow on
 *   the receiving end reads `$json.metadata.*` and injects it into
 *   the LLM prompt as grounding context.
 * - Updating the prop after mount mutates the metadata object that
 *   `@n8n/chat` provided to its Vue components via Vue 3's
 *   provide/inject system. The actual storage path is
 *   `chatInstance._context.provides.ChatOptions.metadata` — verified
 *   in the running widget. This is a private-API mutation against
 *   `@n8n/chat`'s internal Vue tree; it works on the bundled version
 *   we depend on but could break on a major bump. Test after any
 *   CDN URL change. The walk is guarded so a structural change
 *   degrades to a logged warning instead of a crash.
 */

import React from 'react';
import styles from './N8nChat.module.css';
import { getSession } from 'Vulture/Session.js';
import {
	hideSalesforceChat,
	showSalesforceChat,
} from 'Vulture/lib/SalesforceChat.js';

const CHAT_BUNDLE_URL = 'https://cdn.jsdelivr.net/npm/@n8n/chat/chat.bundle.es.js';
const CHAT_STYLES_URL = 'https://cdn.jsdelivr.net/npm/@n8n/chat/style.css';
const CHAT_WEBHOOK_URL = 'https://touchsource.app.n8n.cloud/webhook/66665fdb-cbe7-4074-85f6-567f49a1f531/chat';

const STYLES_ELEMENT_ID = 'n8n-chat-styles';
const SCRIPT_ELEMENT_ID = 'n8n-chat-script';
const FULLSCREEN_BUTTON_ID = 'chat-fullscreen-toggle';
const NEW_CHAT_BUTTON_ID = 'chat-new-chat-toggle';
const HISTORY_BUTTON_ID = 'chat-history-toggle';
const HISTORY_PANEL_ID = 'chat-history-panel';

const STORAGE_VERSION = 1;
const ARCHIVE_CAP = 50;
const PERSIST_INTERVAL_MS = 2000;

const newSessionId = () =>
	(typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID()) ||
	`s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;

class N8nChat extends React.Component {
	hostRef = React.createRef();
	chatInstance = null;
	fullscreenTimer = null;
	readyHandler = null;
	persistTimer = null;
	lastPersistedSnapshot = '';
	userUUID = null;
	storagePrefix = null;

	componentDidMount() {
		// Storage namespace per logged-in user. Falls through to 'anonymous'
		// if there's no session yet (chat still works, but persistence is
		// process-wide rather than user-scoped).
		const session = (typeof getSession === 'function' && getSession()) || {};
		this.userUUID = session.user || 'anonymous';
		this.storagePrefix = `n8n-chat:v${STORAGE_VERSION}:${this.userUUID}`;

		// Only one chatbot per context: while this internal design assistant is
		// mounted, the customer-facing Salesforce widget is hidden — both float
		// in the bottom-right corner and would stack (DEV-11532 QA).
		hideSalesforceChat();

		this.injectStylesheet();
		this.injectBundleScript();

		// `createChat` runs inside the injected ESM module. We bridge to
		// React by listening for a custom event the bundle dispatches once
		// the chat is ready (see injectBundleScript for the dispatch).
		this.readyHandler = (event) => {
			this.chatInstance = event.detail.instance;
			// Restore happens on the same tick as fullscreen-button attach
			// (1000ms after createChat). Earlier than that, the @n8n/chat
			// widget's Vue mount lifecycle hasn't finished — `messages.value`
			// is still being initialized and any splice we do gets reset.
			// Verified empirically: at 250ms our restore runs but the widget
			// clears messages right after; at 1000ms the widget is settled and
			// the splice sticks.
			//
			// We also re-restore on chat-window-toggle click via the toggle
			// listener wired in attachHistoryButtons — if the widget does any
			// additional resets when the chat is opened, the on-open path
			// catches them.
			this.fullscreenTimer = setTimeout(() => {
				this.attachFullscreenToggle();
				this.attachHistoryButtons();
				this.restoreActiveSession();
			}, 1000);
			this.persistTimer = setInterval(this.persistActiveSession, PERSIST_INTERVAL_MS);
		};
		window.addEventListener('n8nChatReady', this.readyHandler);
	}

	componentDidUpdate(prevProps) {
		// Reactive metadata: when the parent passes new context (e.g. user
		// switched designs), mutate the metadata object the chat's Vue
		// components inject via Vue 3's provide/inject. Private-API
		// mutation — see header comment for the path rationale.
		if (this.chatInstance && prevProps.metadata !== this.props.metadata) {
			try {
				const ctx = this.chatInstance._context;
				const opts = ctx && ctx.provides && ctx.provides.ChatOptions;
				if (opts) {
					opts.metadata = this.props.metadata || {};
				} else {
					console.warn('[N8nChat] could not locate ChatOptions in chat instance — n8n may have changed its internal structure');
				}
			} catch (err) {
				console.warn('[N8nChat] failed to update metadata on running chat', err);
			}
		}
	}

	componentWillUnmount() {
		// Persist one last snapshot so anything the user typed in the final
		// seconds before unmount survives.
		this.persistActiveSession();

		if (this.readyHandler) {
			window.removeEventListener('n8nChatReady', this.readyHandler);
			this.readyHandler = null;
		}
		clearTimeout(this.fullscreenTimer);
		clearInterval(this.persistTimer);
		this.persistTimer = null;

		if (this.chatInstance && typeof this.chatInstance.destroy === 'function') {
			this.chatInstance.destroy();
		}
		this.chatInstance = null;

		// Tear down injected DOM. We own these by id, so removal is safe.
		document.getElementById(STYLES_ELEMENT_ID)?.remove();
		document.getElementById(SCRIPT_ELEMENT_ID)?.remove();
		document.querySelector('.n8n-chat')?.remove();
		document.getElementById(FULLSCREEN_BUTTON_ID)?.remove();
		document.getElementById(NEW_CHAT_BUTTON_ID)?.remove();
		document.getElementById(HISTORY_BUTTON_ID)?.remove();
		document.getElementById(HISTORY_PANEL_ID)?.remove();

		if (window.n8nChatInstance) {
			window.n8nChatInstance = null;
		}

		// Leaving the page that owns the internal assistant — the customer-facing
		// Salesforce widget becomes the intended chatbot again.
		showSalesforceChat();
	}

	/* ---------- Per-user chat history ---------- */

	getChatProvider = () => {
		const inst = this.chatInstance;
		return inst && inst._context && inst._context.provides && inst._context.provides.Chat;
	};

	// @n8n/chat exposes `messages` and `currentSessionId` as Vue 3 refs
	// inside the Chat provide bag. We have to go through `.value` to read or
	// mutate them; assigning the wrapper directly would replace the ref and
	// break reactivity.
	getMessagesArray = () => {
		const chat = this.getChatProvider();
		if (!chat || !chat.messages) return null;
		return chat.messages.value !== undefined ? chat.messages.value : chat.messages;
	};

	getSessionId = () => {
		const chat = this.getChatProvider();
		if (!chat || !chat.currentSessionId) return null;
		return chat.currentSessionId.value !== undefined ? chat.currentSessionId.value : chat.currentSessionId;
	};

	setSessionId = (value) => {
		const chat = this.getChatProvider();
		if (!chat || !chat.currentSessionId) return;
		if ('value' in chat.currentSessionId) {
			chat.currentSessionId.value = value;
		} else {
			chat.currentSessionId = value;
		}
	};

	activeKey = () => `${this.storagePrefix}:active`;
	archiveKey = () => `${this.storagePrefix}:archive`;

	loadArchive = () => {
		try {
			return JSON.parse(localStorage.getItem(this.archiveKey()) || '[]') || [];
		} catch (err) {
			console.warn('[N8nChat] could not parse archive', err);
			return [];
		}
	};

	saveArchive = (archive) => {
		try {
			localStorage.setItem(this.archiveKey(), JSON.stringify(archive));
		} catch (err) {
			// Likely QuotaExceeded — drop oldest entries and retry once.
			console.warn('[N8nChat] archive write failed; trimming', err);
			const trimmed = archive.slice(0, Math.max(1, Math.floor(archive.length / 2)));
			try { localStorage.setItem(this.archiveKey(), JSON.stringify(trimmed)); } catch {}
		}
	};

	deriveTitle = (messages) => {
		// Use the first user message as a chat title; fall back to a
		// timestamp when there's nothing yet.
		const firstUser = (messages || []).find((m) => m && m.sender === 'user' && m.text);
		if (firstUser && firstUser.text) {
			return firstUser.text.slice(0, 60).replace(/\s+/g, ' ').trim() + (firstUser.text.length > 60 ? '…' : '');
		}
		return 'Untitled chat';
	};

	restoreActiveSession = () => {
		const messages = this.getMessagesArray();
		if (!messages) return;
		try {
			const raw = localStorage.getItem(this.activeKey());
			if (!raw) return;
			const active = JSON.parse(raw);
			if (!active || !Array.isArray(active.messages) || active.messages.length === 0) return;
			messages.splice(0, messages.length, ...active.messages);
			if (active.sessionId) {
				this.setSessionId(active.sessionId);
			}
		} catch (err) {
			console.warn('[N8nChat] could not restore active session', err);
		}
	};

	persistActiveSession = () => {
		if (!this.storagePrefix) return;
		const messages = this.getMessagesArray();
		if (!messages) return;
		// Skip the trivial initial state (just the canned greeting messages
		// from createChat). Wait until the user has actually contributed.
		const userMessages = messages.filter((m) => m && m.sender === 'user');
		if (userMessages.length === 0) return;

		const snapshot = {
			sessionId: this.getSessionId() || null,
			messages: messages.map((m) => ({ ...m })),
			lastActivity: Date.now(),
			title: this.deriveTitle(messages),
		};
		const serialized = JSON.stringify(snapshot);
		if (serialized === this.lastPersistedSnapshot) return;
		try {
			localStorage.setItem(this.activeKey(), serialized);
			this.lastPersistedSnapshot = serialized;
		} catch (err) {
			console.warn('[N8nChat] active session write failed', err);
		}
	};

	startNewChat = () => {
		const messages = this.getMessagesArray();
		if (!messages) return;
		// Archive the current session if it has any user input.
		if (messages.some((m) => m && m.sender === 'user')) {
			const archive = this.loadArchive();
			archive.unshift({
				sessionId: this.getSessionId() || newSessionId(),
				messages: messages.map((m) => ({ ...m })),
				archivedAt: Date.now(),
				title: this.deriveTitle(messages),
			});
			if (archive.length > ARCHIVE_CAP) archive.length = ARCHIVE_CAP;
			this.saveArchive(archive);
		}
		// Clear active state.
		messages.splice(0, messages.length);
		this.setSessionId(newSessionId());
		this.lastPersistedSnapshot = '';
		try { localStorage.removeItem(this.activeKey()); } catch {}
		// Re-render the history popover if it's open so the new entry shows.
		this.renderHistoryPanel();
	};

	loadArchivedChat = (sessionId) => {
		const archive = this.loadArchive();
		const entry = archive.find((e) => e.sessionId === sessionId);
		if (!entry) return;
		// Archive whatever's currently active first so we don't lose it.
		this.startNewChat();
		// Then replace messages + sessionId with the chosen archive.
		const messages = this.getMessagesArray();
		if (messages) {
			messages.splice(0, messages.length, ...(entry.messages || []));
		}
		this.setSessionId(entry.sessionId);
		this.persistActiveSession();
		this.closeHistoryPanel();
	};

	deleteArchivedChat = (sessionId) => {
		const archive = this.loadArchive().filter((e) => e.sessionId !== sessionId);
		this.saveArchive(archive);
		this.renderHistoryPanel();
	};

	/* ---------- Header buttons + history popover (DOM injection) ---------- */

	attachHistoryButtons = () => {
		const chatHeader = document.querySelector('.chat-header');
		if (!chatHeader) return;

		// Belt-and-braces: re-run restore the first time the chat window is
		// opened. The widget seems to do its real Vue mount lazily on first
		// open, and on slow machines a 1000ms restore from createChat may
		// still race the mount. Hooking the toggle click guarantees restore
		// happens after the user's first interaction at the latest.
		const openToggle = document.querySelector('.chat-window-toggle');
		if (openToggle && !openToggle.dataset.restoreHooked) {
			openToggle.dataset.restoreHooked = '1';
			openToggle.addEventListener('click', () => {
				// Tiny defer so the open animation lets the message list mount.
				setTimeout(() => {
					const messages = this.getMessagesArray();
					// Only restore if the user hasn't typed anything in this
					// open session — avoid clobbering an in-progress chat.
					if (messages && !messages.some((m) => m && m.sender === 'user')) {
						this.restoreActiveSession();
					}
				}, 100);
			});
		}

		// Match the visual style of the existing fullscreen button.
		const mkBtn = (id, title, svg, onClick) => {
			const b = document.createElement('button');
			b.id = id;
			b.className = 'chat-fullscreen-button';
			b.title = title;
			b.innerHTML = svg;
			b.onclick = onClick;
			return b;
		};

		if (!document.getElementById(NEW_CHAT_BUTTON_ID)) {
			const newBtn = mkBtn(
				NEW_CHAT_BUTTON_ID,
				'New chat',
				'<svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/></svg>',
				() => this.startNewChat()
			);
			// Place before the fullscreen toggle so the order reads "+ history fullscreen".
			const fullBtn = document.getElementById(FULLSCREEN_BUTTON_ID);
			if (fullBtn) {
				chatHeader.insertBefore(newBtn, fullBtn);
			} else {
				chatHeader.appendChild(newBtn);
			}
		}

		if (!document.getElementById(HISTORY_BUTTON_ID)) {
			const histBtn = mkBtn(
				HISTORY_BUTTON_ID,
				'Past chats',
				'<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>',
				() => this.toggleHistoryPanel()
			);
			const fullBtn = document.getElementById(FULLSCREEN_BUTTON_ID);
			if (fullBtn) {
				chatHeader.insertBefore(histBtn, fullBtn);
			} else {
				chatHeader.appendChild(histBtn);
			}
		}
	};

	toggleHistoryPanel = () => {
		const existing = document.getElementById(HISTORY_PANEL_ID);
		if (existing) {
			this.closeHistoryPanel();
		} else {
			this.renderHistoryPanel({ open: true });
		}
	};

	closeHistoryPanel = () => {
		document.getElementById(HISTORY_PANEL_ID)?.remove();
	};

	renderHistoryPanel = ({ open } = {}) => {
		const existing = document.getElementById(HISTORY_PANEL_ID);
		if (!existing && !open) return; // not currently shown, don't force-open on background updates
		existing?.remove();

		const chatBody = document.querySelector('.chat-body') || document.querySelector('.chat-window');
		if (!chatBody) return;

		const panel = document.createElement('div');
		panel.id = HISTORY_PANEL_ID;
		panel.className = styles.historyPanel;

		const archive = this.loadArchive();
		if (archive.length === 0) {
			panel.innerHTML = '<div class="' + styles.historyEmpty + '">No past chats yet.</div>';
		} else {
			const list = document.createElement('div');
			list.className = styles.historyList;
			archive.forEach((entry) => {
				const row = document.createElement('div');
				row.className = styles.historyRow;

				const main = document.createElement('button');
				main.className = styles.historyRowMain;
				main.title = 'Restore this chat';
				const title = document.createElement('div');
				title.className = styles.historyRowTitle;
				title.textContent = entry.title || 'Untitled chat';
				const meta = document.createElement('div');
				meta.className = styles.historyRowMeta;
				const dt = entry.archivedAt ? new Date(entry.archivedAt) : null;
				meta.textContent = dt ? dt.toLocaleString() : '';
				main.appendChild(title);
				main.appendChild(meta);
				main.onclick = () => this.loadArchivedChat(entry.sessionId);

				const del = document.createElement('button');
				del.className = styles.historyRowDelete;
				del.title = 'Delete this chat';
				del.innerHTML = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>';
				del.onclick = (e) => {
					e.stopPropagation();
					this.deleteArchivedChat(entry.sessionId);
				};

				row.appendChild(main);
				row.appendChild(del);
				list.appendChild(row);
			});
			panel.appendChild(list);
		}

		chatBody.appendChild(panel);
	};

	injectStylesheet() {
		if (document.getElementById(STYLES_ELEMENT_ID)) {
			return;
		}
		const link = document.createElement('link');
		link.id = STYLES_ELEMENT_ID;
		link.rel = 'stylesheet';
		link.href = CHAT_STYLES_URL;
		document.head.appendChild(link);
	}

	injectBundleScript() {
		if (document.getElementById(SCRIPT_ELEMENT_ID) || window.n8nChatInstance) {
			return;
		}

		const initialMetadata = JSON.stringify(this.props.metadata || {});
		const subtitle = this.subtitleFromMetadata();

		const script = document.createElement('script');
		script.id = SCRIPT_ELEMENT_ID;
		script.type = 'module';
		// The bundle is an ESM URL; webpack can't resolve dynamic imports of
		// remote URLs in this CRA toolchain. Injecting a `<script type=module>`
		// is the supported pattern from @n8n/chat for CDN consumers.
		script.textContent = `
			import { createChat } from '${CHAT_BUNDLE_URL}';
			if (!window.n8nChatInstance) {
				window.n8nChatInstance = createChat({
					webhookUrl: '${CHAT_WEBHOOK_URL}',
					initialMessages: [
						'Hi there! 👋',
						'I am your AI assistant. Ask me about the design or system you are currently looking at — I can see its config in context.'
					],
					mode: 'window',
					chatInputKey: 'chatInput',
					chatSessionKey: 'sessionId',
					metadata: ${initialMetadata},
					showWelcomeScreen: false,
					defaultLanguage: 'en',
					i18n: {
						en: {
							title: 'Spark PX Design Assistant',
							subtitle: ${JSON.stringify(subtitle)},
							footer: '',
							getStarted: 'Start Chat',
							inputPlaceholder: 'Type your question...'
						}
					}
				});
				window.dispatchEvent(new CustomEvent('n8nChatReady', { detail: { instance: window.n8nChatInstance } }));
			}
		`;
		document.head.appendChild(script);
	}

	subtitleFromMetadata = () => {
		const m = this.props.metadata;
		if (!m) {
			return 'Ask me anything';
		}
		if (m.design_name) {
			return `Looking at: ${m.design_name}`;
		}
		if (m.active_system_uuid) {
			return `System: ${m.active_system_uuid.slice(0, 8)}…`;
		}
		return 'Ask me anything';
	};

	attachFullscreenToggle = () => {
		const chatHeader = document.querySelector('.chat-header');
		if (!chatHeader || document.getElementById(FULLSCREEN_BUTTON_ID)) {
			return;
		}

		// Wrap h1 + p in a flex container so they stack cleanly next to the toggle.
		const h1 = chatHeader.querySelector('h1');
		const p = chatHeader.querySelector('p');
		if (h1 && p && !chatHeader.querySelector('.chat-header-text')) {
			const textContainer = document.createElement('div');
			textContainer.className = 'chat-header-text';
			chatHeader.insertBefore(textContainer, chatHeader.firstChild);
			textContainer.appendChild(h1);
			textContainer.appendChild(p);
		}

		const expandIcon = '<svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"></path></svg>';
		const collapseIcon = '<svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"></path></svg>';

		const toggleButton = document.createElement('button');
		toggleButton.id = FULLSCREEN_BUTTON_ID;
		toggleButton.className = 'chat-fullscreen-button';
		toggleButton.innerHTML = expandIcon;
		toggleButton.title = 'Toggle fullscreen';
		toggleButton.onclick = () => {
			const chatWrapper = document.querySelector('.chat-window-wrapper');
			if (!chatWrapper) {
				return;
			}
			chatWrapper.classList.toggle('chat-fullscreen');
			const isFullscreen = chatWrapper.classList.contains('chat-fullscreen');
			toggleButton.innerHTML = isFullscreen ? collapseIcon : expandIcon;
			toggleButton.title = isFullscreen ? 'Exit fullscreen' : 'Toggle fullscreen';
		};
		chatHeader.appendChild(toggleButton);
	};

	render() {
		// The widget renders itself as a floating window via @n8n/chat's
		// own DOM injection. We render a no-op host so this component owns
		// a place in the React tree (and so consumers can pass refs/keys).
		return <div ref={this.hostRef} className={styles.host} aria-hidden='true' />;
	}
}

export default N8nChat;
